<script lang="ts">
  import { PUBLIC_BASE_URL } from '$env/static/public';
  import { browser } from "$app/environment";
  import type { PageData } from "./$types";
  import type { Entry } from 'contentful';
  import type { IStaticPage, IFaq, INews } from '$lib/utils/contentful/types';
  import { StructureUtils } from '$lib/utils/structured-utils';
  import { GtmStore } from '$lib/utils/gtm-store';
  // Components
  import { Head } from '$lib/components/atoms/Head'
  import { AnchorTabs } from '$lib/components/atoms/AnchorTabs';
  import { Accordion } from '$lib/components/atoms/Accordion';
  import { LinkTo } from '$lib/components/atoms/Link';
  import { Breadcrumb, BreadcrumbItem } from '$lib/components/molecules/Breadcrumb';
	import { HeroStandard } from '$lib/components/organisms/Hero';
  import { Grid } from '$lib/components/organisms/Layout';
	import { CardContainer, Partners, Section, SectionProduct, SectionTight, SectionFaq, SimpleCardsGrid } from '$lib/components/organisms/Section';
	import { ArticleCard, DetailCard } from '$lib/components/organisms/Card';
	import { B2BRequestInfoFormContainer } from '$lib/components/organisms/Section';

  export let data: PageData;

  const metaTagData: IStaticPage | null = data?.metaTagData ? data.metaTagData : null;
  const b2bFaq: IFaq[] = data?.b2bFaq ? data.b2bFaq : [];
  const b2bNews: Entry<INews>[] = data?.b2bNews && data?.b2bNews.length > 0 ? data.b2bNews.slice(0, 3) : [];

  let hasFaq: boolean = false;
  
  /******************** GTM EVENT PUSH *************************/
  GtmStore.pushEvent({
    event: 'pageview',
    pageCategory: 'B2B_PerLeImprese',
  });
  /*************************************************************/

  const breadcrumbJson = StructureUtils.getBreadcrumbStructure(
    [
      {
        name: 'Home',
        url: PUBLIC_BASE_URL,
      },
      {
        name: 'Imprese',
        url: `${PUBLIC_BASE_URL}b2b`,
      }
    ],
    true
  );

  let ldScriptTag = '';
  ldScriptTag = StructureUtils.createScriptTag(JSON.stringify([breadcrumbJson, b2bFaq]));

  const seoDetails = {
    title: metaTagData?.fields?.metaTagSocialTitle || 'Servizi per le imprese, formazione e partnership',
    description: metaTagData?.fields?.metaTagSocialDescription || 'Scopri i servizi personalizzati per le aziende, formiamo insieme i manager del futuro.',
    metaTagSocialTitle: metaTagData?.fields?.metaTagSocialTitle || '',
    metaTagSocialDescription: metaTagData?.fields?.metaTagSocialDescription || '',
    url: '/b2b',
    image: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
    metaTagSocialImage: metaTagData?.fields?.metaTagSocialImage?.fields.file.url || '',
    ldScriptTag: ldScriptTag,
  };

  $: hasFaq = b2bFaq && b2bFaq.length > 0;
</script>

<Head appendCompany {seoDetails} />

<Breadcrumb disableLastElementLink>
  <BreadcrumbItem>
    <a href="/">Home</a>
  </BreadcrumbItem>
  <BreadcrumbItem current>
    <a href="/b2b">Imprese</a>
  </BreadcrumbItem>
</Breadcrumb>

<HeroStandard
  darkOverlay
  data={{
    image: "/assets/images/b2b/per_le_imprese_header_desktop.jpg",
    srcset:'/assets/images/b2b/per_le_imprese_header_mobile.jpg 768w, /assets/images/b2b/per_le_imprese_header_desktop.jpg',
    imageAlt: 'Formazione aziendale e Servizi ad alto valore aggiunto per le imprese',
    subtitle: 'SERVIZI PER LE AZIENDE',
    title: 'Formazione aziendale e Servizi ad alto valore aggiunto per le imprese',
  }}
  taller
/>

<div class="anchor-tabs-container">
  <Grid class="bg-white">
    <div class="col-span-full">
      <AnchorTabs offset={176}>
        <a href="#i-nostri-servizi">I nostri servizi</a>
        {#if hasFaq}<a href="#faq">Faq</a>{/if}
        <a href="#contatti">Contatti</a>
      </AnchorTabs>
    </div>
  </Grid>
</div>

<Section noPaddingTop noPaddingBottom class="b2b">

  <SectionTight compact tighter variant="transparent" class="pt-3 pb-6 md:pt-6 bg-gray-50">
    <div slot="header">
      <p class="py-6">
        <strong>24ORE Business School</strong>, da quasi 30 anni punto di riferimento nel panorama della <strong>formazione aziendale</strong>. Formiamo <strong>dirigenti, manager, middle, junior</strong> aiutandoli a definire e a realizzare i loro <strong>progetti di crescita personale e professionale</strong> con un’offerta completa, innovativa, sempre allineata con le esigenze del mercato.<br>
        Siamo la prima scuola di formazione italiana che offre percorsi formativi per le aziende sulle principali <strong>Industry di riferimento</strong>. Collaboriamo con <strong>docenti di estrazione aziendale</strong>, con esperienza diretta di settore, approccio pragmatico e business oriented, che portano le loro testimonianze di eccellenza, fondamentali per garantire un alto livello di coinvolgimento e aumentare il valore aggiunto della nostra proposta formativa.<br>
        Crediamo nel grande <strong>valore</strong> della formazione aziendale, essenziale per garantire la <strong>crescita del business</strong> e proiettare l’azienda nel <strong>futuro</strong>.
      </p>
    </div>
  </SectionTight>

  <Grid noGrid>
    <SectionProduct variant="transparent" class="pt-6 pb-6 md:pt-6">
      <h2 class="section-title mb-2">Il nostro modello WIN-WIN</h2>
      <div class="py-6">
        <Grid noHorizontalPadding>
          <div class="col-span-full md:col-span-3">
            <div class="model-win-win-container">
              <img src="/assets/images/b2b/30anni-150x150.svg" alt="Più di 30 anni di storia nel mondo della corporate education"/>
              <h4>+30 anni</h4>
              <p>di storia nel mondo della corporate education</p>
            </div>
          </div>
          <div class="col-span-full md:col-span-3 text-center">
            <div class="model-win-win-container">
              <img src="/assets/images/b2b/catalogo-150x150.svg" alt="Oltre 2.000 aziende all’anno che ci scelgono per la formazione a catalogo"/>
              <h4>Oltre 2.000</h4>
              <p>aziende all’anno che ci scelgono per la formazione a catalogo</p>
            </div>
          </div>
          <div class="col-span-full md:col-span-3 text-center">
            <div class="model-win-win-container">
              <img src="/assets/images/b2b/progetti-150x150.svg" alt="Più di 80 progetti formativi all’anno costruiti e realizzati su misura per le aziende"/>
              <h4>+80</h4>
              <p>progetti formativi all’anno costruiti e realizzati su misura per le aziende</p>
            </div>
          </div>
          <div class="col-span-full md:col-span-3 text-center">
            <div class="model-win-win-container">
              <img src="/assets/images/b2b/1000aziende-150x150.svg" alt="Un network di partnerhip di oltre 1000 aziende"/>
              <h4>Oltre 1.000</h4>
              <p>Aziende nel nostro network di partnerhip</p>
            </div>
          </div>
        </Grid>
      </div>
    </SectionProduct>
  </Grid>

  <Grid noGrid>
    <SectionProduct id="i-nostri-servizi" variant="transparent" class="pt-3 pb-6 md:pt-6">
      <p class="subtitle v-small mb-2">I NOSTRI SERVIZI</p>
      <h2 class="section-title mb-2">Un Servizio per ogni esigenza Corporate</h2>
      <div class="py-6">
        <SimpleCardsGrid columns="4">
          <DetailCard
            data={{
              thumbnailImage: "/assets/images/b2b/per_le_imprese_skilling.jpg",
              title: "Skilling",
              abstract: "Investi nella formazione dei tuoi collaboratori: scegli i percorsi più adatti alla tua azienda tra i corsi presenti nella nostra offerta formativa, o affidati al nostro staff per la creazione di un percorso su misura.",
              ctaText: "SCOPRI DI PIÙ",
              href: "/b2b/skilling"
            }}
          />
          <DetailCard
            data={{
              thumbnailImage: '/assets/images/b2b/HP_box_04_Finance.jpg',
              title: 'Finance',
              abstract: 'Scopri le opportunità di finanziamento per sostenere gli interventi formativi, così da garantire un processo di formazione continua a zero costi o costi contenuti.',
              ctaText: 'SCOPRI DI PIÙ',
              href: '/b2b/finance',
            }}
          />
          <DetailCard
            data={{
              thumbnailImage: "/assets/images/b2b/per_le_imprese_job_finding.jpg",
              title: "Job Finding",
              abstract: "Trova un nuovo talento per la tua azienda attivando con noi uno stage curriculare, condividendo le opportunità lavorative aperte o partecipando ai nostri recruiting day.",
              ctaText: "SCOPRI DI PIÙ",
              href: "/b2b/job-finding"
            }}
          />
          <DetailCard
            data={{
              thumbnailImage: "/assets/images/b2b/per_le_imprese_branding.jpg",
              title: "Branding",
              abstract: "Porta nelle nostre aule valore ed esperienza trasferendo ai nostri partecipanti il know how e contribuisci così al posizionamento strategico del tuo brand.",
              ctaText: "SCOPRI DI PIÙ",
              href: "/b2b/branding"
            }}
          />
        </SimpleCardsGrid>
      </div>
    </SectionProduct>
  </Grid>

  {#if hasFaq}
    <Section id="faq" compact class="pt-3 pb-6 md:pt-6 bg-tint text-contrast-tint">
      <div class="py-6">
      <Grid>
        <div class="col-span-full md:col-span-4">
          <p class="subtitle v-small mb-2 text-white">FAQ</p>
          <h2 class="section-title mb-2 text-white">Domande frequenti</h2>
        </div>
        <div class="col-span-full md:col-span-8">
          <SectionFaq>
            {#each faq as currentFaq}
              <Accordion
                variant="light"
                entry={{
                  question: currentFaq.question,
                  answer: currentFaq.answerHtml,
                }}
                offset={60}
              />
            {/each}
          </SectionFaq>
        </div>
      </Grid>
    </Section>
  {/if}

  <Section compact class="bg-gray-50">
    <svelte:fragment slot="header">
      <h3>Le ultime novità dal mondo delle imprese</h3>
    </svelte:fragment>

    {#if b2bNews && b2bNews.length > 0}
      <CardContainer>
        {#each b2bNews as article, index}
          <ArticleCard {article} />
        {/each}
      </CardContainer>
    {/if}
  </Section>

  <Section>
    <div class="flex" slot="header">
      <h2 class="caption-title md:mx-auto">I nostri partner</h2>
    </div>
    <Partners>
      <img
        src="/assets/placeholders/mobile/partners/n26.webp"
        srcset="/assets/placeholders/desktop/partners/n26.webp 1x, /assets/placeholders/high/partners/n26.webp 2x"
        alt="N26"
      />
      <img
        src="/assets/placeholders/mobile/partners/accenture.webp"
        srcset="/assets/placeholders/desktop/partners/accenture.webp 1x, /assets/placeholders/high/partners/accenture.webp 2x"
        alt="accenture"
      />
      <img
        src="/assets/placeholders/mobile/partners/bmw.webp"
        srcset="/assets/placeholders/desktop/partners/bmw.webp 1x, /assets/placeholders/high/partners/bmw.webp 2x"
        alt="BMW"
      />
      <img
        src="/assets/placeholders/mobile/partners/samsung.webp"
        srcset="/assets/placeholders/desktop/partners/samsung.webp 1x, /assets/placeholders/high/partners/samsung.webp 2x"
        alt="Samsung"
      />
      <img
        src="/assets/placeholders/mobile/partners/enel.webp"
        srcset="/assets/placeholders/desktop/partners/enel.webp 1x, /assets/placeholders/high/partners/enel.webp 2x"
        alt="Enel"
      />
      <img
        src="/assets/placeholders/mobile/partners/booking.webp"
        srcset="/assets/placeholders/desktop/partners/booking.webp 1x, /assets/placeholders/high/partners/booking.webp 2x"
        alt="booking.com"
      />
      <img
        src="/assets/placeholders/mobile/partners/mediolanum.webp"
        srcset="/assets/placeholders/desktop/partners/mediolanum.webp 1x, /assets/placeholders/high/partners/mediolanum.webp 2x"
        alt="Mediolanum"
      />
      <img
        src="/assets/placeholders/mobile/partners/sky.webp"
        srcset="/assets/placeholders/desktop/partners/sky.webp 1x, /assets/placeholders/high/partners/sky.webp 2x"
        alt="Sky"
      />
    </Partners>
    <LinkTo slot="footer" href="/partners">Vedi tutti i partner</LinkTo>
  </Section>

  <Section id="contatti" class="bg-gray-50">
    <B2BRequestInfoFormContainer targetB2B="le-imprese"/>
  </Section>

</Section>

<style type="text/scss" lang="scss" scoped>
  .anchor-tabs-container {
    @apply bg-white;
    position: sticky;
    top: 96px;
    z-index: 80;
  }
  .model-win-win-container {
    @apply text-center;
    img {
      @apply m-auto;
      @apply h-32;
    }
    h4 {
      @apply text-lg font-bold pt-4 pb-2;
    }
    p {
      @apply pb-12 md:pb-0
    }
  }
  .section-title {
    font-size: 2.5rem;
    line-height: 1;
  }
</style>